import React, { useState } from "react";
import { Trash2, Users } from "react-feather";
import { useGroups } from "./useGroups";
import { useRealtime } from "../realtime/useRealtime";

// Desktop version of the groups manager. It lives in Settings -> Groups.
const GroupCard = ({ group, api, tokens, fonts }) => {
  const [name, setName] = useState(group.name);
  const [query, setQuery] = useState("");
  const [found, setFound] = useState(undefined); // undefined = not searched, null = nobody
  const [message, setMessage] = useState("");

  const field = { padding: "9px 12px", fontSize: 14, borderRadius: 8, border: `1px solid ${tokens.surfaceBorder}`, backgroundColor: tokens.bg, color: tokens.textPrimary, fontFamily: "inherit" };
  const btn = (kind) => ({
    padding: "8px 14px",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    border: kind === "primary" ? "none" : `1px solid ${kind === "danger" ? tokens.negative : tokens.surfaceBorder}`,
    backgroundColor: kind === "primary" ? tokens.accent : "transparent",
    color: kind === "primary" ? tokens.accentContrast : kind === "danger" ? tokens.negative : tokens.textPrimary,
  });

  const run = (fn) => {
    setMessage("");
    return fn().catch((err) => setMessage(err.message));
  };

  const search = (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setFound(undefined);
    run(async () => setFound(await api.search(query.trim())));
  };

  const add = () =>
    run(async () => {
      await api.addMember(group.id, found.email || found.username);
      setFound(undefined);
      setQuery("");
    });

  const alreadyIn = found && group.members.some((m) => m.username === found.username);

  // A group someone else made and added me to: read-only, with a way to leave.
  if (!group.isOwner) {
    return (
      <div style={{ padding: 20, borderRadius: 14, backgroundColor: tokens.surface, border: `1px solid ${tokens.surfaceBorder}`, boxShadow: tokens.cardShadow }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
          <Users size={18} color={tokens.accent} />
          <span style={{ flex: 1, fontSize: 15, fontWeight: 700, color: tokens.textPrimary, fontFamily: fonts.display }}>{group.name}</span>
          <span style={{ fontSize: 12, color: tokens.textMuted }}>Created by {group.ownerName}</span>
          <button style={btn("danger")} onClick={() => { if (window.confirm(`Leave the group "${group.name}"?`)) run(() => api.leave(group.id)); }}>Leave group</button>
        </div>
        {group.members.map((m) => (
          <div key={m.userId} style={{ padding: "8px 0", borderTop: `1px solid ${tokens.surfaceBorder}`, fontSize: 14, color: tokens.textPrimary }}>
            {m.username}
            {m.username === group.ownerName && <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, color: tokens.accent }}>OWNER</span>}
          </div>
        ))}
        {message && <div role="alert" style={{ marginTop: 10, fontSize: 13, color: tokens.negative }}>{message}</div>}
      </div>
    );
  }

  return (
    <div style={{ padding: 20, borderRadius: 14, backgroundColor: tokens.surface, border: `1px solid ${tokens.surfaceBorder}`, boxShadow: tokens.cardShadow }}>
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
        <Users size={18} color={tokens.accent} />
        <input style={{ ...field, flex: 1, minWidth: 160, fontWeight: 700 }} value={name} onChange={(e) => setName(e.target.value)} maxLength={50} aria-label="Group name" />
        {name.trim() && name.trim() !== group.name && <button style={btn("primary")} onClick={() => run(() => api.rename(group.id, name))}>Save name</button>}
        <button style={btn("danger")} onClick={() => { if (window.confirm(`Delete the group "${group.name}"? People stay on tasks that are already shared.`)) run(() => api.remove(group.id)); }}>Delete group</button>
      </div>

      {group.members.length === 0 && <p style={{ margin: "0 0 12px", fontSize: 13, color: tokens.textMuted }}>No one yet. Search for someone below.</p>}
      {group.members.map((m) => (
        <div key={m.userId} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 0", borderTop: `1px solid ${tokens.surfaceBorder}` }}>
          <div style={{ flex: 1 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: tokens.textPrimary }}>{m.username}</span>
            {m.email && <span style={{ marginLeft: 10, fontSize: 12, color: tokens.textMuted }}>{m.email}</span>}
          </div>
          <button onClick={() => run(() => api.removeMember(group.id, m.userId))} aria-label={`Remove ${m.username}`} style={{ background: "none", border: "none", cursor: "pointer", color: tokens.negative }}>
            <Trash2 size={16} />
          </button>
        </div>
      ))}

      <form onSubmit={search} style={{ display: "flex", gap: 8, marginTop: 14 }}>
        <input style={{ ...field, flex: 1 }} value={query} onChange={(e) => { setQuery(e.target.value); setFound(undefined); }} placeholder="Find someone by their full email or username" autoCapitalize="none" />
        <button type="submit" style={btn("ghost")} disabled={!query.trim()}>Search</button>
      </form>

      {found === null && <p style={{ margin: "10px 0 0", fontSize: 13, color: tokens.textMuted }}>No one found with that email or username.</p>}
      {found && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10, padding: 12, borderRadius: 10, backgroundColor: tokens.neutralSoft }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: tokens.textPrimary, fontFamily: fonts.display }}>{found.username}</div>
            <div style={{ fontSize: 12, color: tokens.textMuted }}>{found.email}</div>
          </div>
          {alreadyIn ? <span style={{ fontSize: 12, color: tokens.textMuted }}>Already in this group</span> : <button style={btn("primary")} onClick={add}>Add to group</button>}
        </div>
      )}
      {message && <div role="alert" style={{ marginTop: 10, fontSize: 13, color: tokens.negative }}>{message}</div>}
    </div>
  );
};

const GroupsPanel = ({ tokens, fonts }) => {
  const api = useGroups();
  const reloadGroups = api.reload;
  useRealtime((event) => {
    if (event.kind === "groups" || event.kind === "resync") reloadGroups();
  });
  const [name, setName] = useState("");
  const [error, setError] = useState("");

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setError("");
    try {
      await api.create(name.trim());
      setName("");
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 760 }}>
      <p style={{ margin: 0, fontSize: 13, color: tokens.textMuted }}>
        Groups are the people you can share tasks with. Find someone by their exact email or username, then add them to a group.
      </p>

      <form onSubmit={create} style={{ display: "flex", gap: 8 }}>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="New group name, e.g. Family"
          maxLength={50}
          style={{ flex: 1, padding: "10px 12px", fontSize: 14, borderRadius: 8, border: `1px solid ${tokens.surfaceBorder}`, backgroundColor: tokens.surface, color: tokens.textPrimary, fontFamily: "inherit" }}
        />
        <button type="submit" disabled={!name.trim()} style={{ padding: "10px 18px", border: "none", borderRadius: 8, fontWeight: 700, cursor: "pointer", backgroundColor: tokens.accent, color: tokens.accentContrast, opacity: name.trim() ? 1 : 0.6 }}>
          Create group
        </button>
      </form>
      {error && <div role="alert" style={{ fontSize: 13, color: tokens.negative }}>{error}</div>}

      {api.loading && <p style={{ color: tokens.textMuted }}>Loading…</p>}
      {!api.loading && api.groups.length === 0 && <p style={{ color: tokens.textMuted, fontSize: 14 }}>No groups yet.</p>}
      {api.groups.map((g) => <GroupCard key={g.id} group={g} api={api} tokens={tokens} fonts={fonts} />)}
    </div>
  );
};

export default GroupsPanel;
